import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { toast } from 'react-hot-toast';
import { Cloud, Check, AlertCircle, AlertTriangle, Shield, FileText, FileSpreadsheet, MessageSquare, Mail, Info } from 'lucide-react';
import Label from '../../../components/ui/Label';
import settingsService from '../../../services/settingsService';
import { useAuth } from '../../../contexts/AuthContext';

const IntegrationSettings = () => {
  const { user } = useAuth();
  
  const [apiSettings, setApiSettings] = useState({
    enableApi: true,
    apiVersion: 'v1',
    rateLimitEnabled: true,
    requestsPerMinute: 1000,
    requireAuthentication: true,
    allowCors: true,
    corsOrigins: 'https://app.company.com, https://dashboard.company.com',
    webhooksEnabled: true,
    maxWebhooks: 10
  });

  const [authenticationSettings, setAuthenticationSettings] = useState({
    apiKeyAuth: true,
    oauthEnabled: true,
    jwtEnabled: true,
    basicAuthEnabled: false,
    tokenExpiration: 3600, // seconds
    refreshTokens: true,
    ssoEnabled: false,
    ssoProvider: 'saml',
    ldapEnabled: false
  });

  const [webhookEndpoints, setWebhookEndpoints] = useState([
    {
      id: 1,
      name: 'Document Change Notifications',
      url: 'https://api.company.com/webhooks/documents',
      events: ['document.created', 'document.updated', 'document.deleted'],
      active: true,
      secret: 'whsec_1234567890abcdef',
      lastTriggered: new Date(Date.now() - 3600000)
    },
    {
      id: 2,
      name: 'User Activity Tracking',
      url: 'https://analytics.company.com/webhooks/activity',
      events: ['user.login', 'user.logout', 'document.viewed'],
      active: true,
      secret: 'whsec_abcdef1234567890',
      lastTriggered: new Date(Date.now() - 1800000)
    }
  ]);

  const [externalServices, setExternalServices] = useState({
    microsoftOffice: {
      enabled: false,
      clientId: '',
      tenantId: '',
      configured: false
    },
    googleWorkspace: {
      enabled: false,
      clientId: '',
      projectId: '',
      configured: false
    },
    slack: {
      enabled: true,
      webhookUrl: 'https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXXXXXXXXXXXXXXXXXX',
      channel: '#document-updates',
      configured: true
    },
    email: {
      enabled: true,
      smtpServer: 'smtp.company.com',
      smtpPort: 587,
      username: 'noreply@company.com',
      configured: true
    }
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [microsoftConfig, setMicrosoftConfig] = useState({
    clientId: '',
    clientSecret: '',
    tenantId: '',
    siteId: '',
    authType: 'client_credentials',
    permissions: ['Sites.Selected'],
    configured: false,
  });
  const [savingMicrosoft, setSavingMicrosoft] = useState(false);

  const apiVersionOptions = [
    { value: 'v1', label: 'Version 1.0' },
    { value: 'v2', label: 'Version 2.0 (Beta)' }
  ];

  const ssoProviderOptions = [
    { value: 'saml', label: 'SAML 2.0' },
    { value: 'oidc', label: 'OpenID Connect' },
    { value: 'oauth2', label: 'OAuth 2.0' }
  ];

  const availableEvents = [
    { value: 'document.created', label: 'Document Created' },
    { value: 'document.updated', label: 'Document Updated' },
    { value: 'document.deleted', label: 'Document Deleted' },
    { value: 'document.viewed', label: 'Document Viewed' },
    { value: 'user.login', label: 'User Login' },
    { value: 'user.logout', label: 'User Logout' },
    { value: 'user.created', label: 'User Created' },
    { value: 'change.submitted', label: 'Change Request Submitted' },
    { value: 'change.approved', label: 'Change Request Approved' },
    { value: 'change.rejected', label: 'Change Request Rejected' }
  ];

  const handleApiChange = (field, value) => {
    setApiSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleAuthChange = (field, value) => {
    setAuthenticationSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleServiceChange = (service, field, value) => {
    setExternalServices(prev => ({
      ...prev,
      [service]: {
        ...prev?.[service],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleWebhookUpdate = (id, field, value) => {
    setWebhookEndpoints(prev => prev?.map(webhook => 
      webhook?.id === id ? { ...webhook, [field]: value } : webhook
    ));
    setHasChanges(true);
  };

  const addWebhookEndpoint = () => {
    const newWebhook = {
      id: Date.now(),
      name: 'New Webhook',
      url: '',
      events: [],
      active: false,
      secret: `whsec_${Math.random()?.toString(36)?.substring(2, 18)}`,
      lastTriggered: null
    };
    setWebhookEndpoints(prev => [...prev, newWebhook]);
    setHasChanges(true);
  };

  const removeWebhookEndpoint = (id) => {
    setWebhookEndpoints(prev => prev?.filter(webhook => webhook?.id !== id));
    setHasChanges(true);
  };

  const testWebhook = (id) => {
    console.log(`Testing webhook ${id}`);
    // Mock webhook test
    alert('Webhook test sent successfully!');
  };

  const generateApiKey = () => {
    const apiKey = `zd_${Math.random()?.toString(36)?.substring(2, 32)}`;
    navigator.clipboard?.writeText(apiKey);
    alert(`API Key generated and copied to clipboard: ${apiKey}`);
  };

  const handleSave = () => {
    console.log('Saving integration settings:', {
      apiSettings,
      authenticationSettings,
      webhookEndpoints,
      externalServices
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    // Reset to default values
    setHasChanges(false);
  };

  useEffect(() => {
    if (user?.id) {
      loadSettings();
    }
  }, [user?.id]);

  const loadSettings = async () => {
    try {
      if (!user?.id) {
        console.error('No user ID available for loading settings');
        return;
      }

      // Load Microsoft Graph credentials
      const microsoftSettings = await settingsService?.getSettings(user?.id, 'microsoft');
      
      if (microsoftSettings?.graph_credentials) {
        setMicrosoftConfig(microsoftSettings?.graph_credentials);
      }

      // Load other integration settings as needed
      // const otherSettings = await settingsService?.getSettings(user?.id, 'other_category');

    } catch (error) {
      console.error('Error loading settings:', error);
      toast?.error('Failed to load integration settings');
    }
  };

  const handleSaveMicrosoftConfig = async () => {
    if (!user?.id) {
      toast?.error('User authentication required');
      return;
    }

    setSavingMicrosoft(true);
    try {
      await settingsService?.upsertSetting(
        user?.id,
        'microsoft',
        'graph_credentials',
        {
          ...microsoftConfig,
          configured: true,
        }
      );

      toast?.success('Microsoft Graph configuration saved successfully');
      setMicrosoftConfig(prev => ({ ...prev, configured: true }));
    } catch (error) {
      console.error('Error saving Microsoft configuration:', error);
      toast?.error(error?.message || 'Failed to save Microsoft Graph configuration');
    } finally {
      setSavingMicrosoft(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 🔥 MICROSOFT GRAPH CONFIGURATION - MOVED TO TOP FOR VISIBILITY */}
      <div className="bg-gradient-to-br from-primary/20 via-primary/10 to-primary/5 border-2 border-primary rounded-xl p-8 shadow-lg">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center shadow-lg">
            <Cloud className="text-white" size={32} />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground">
              Microsoft Graph API Configuration
            </h2>
            <p className="text-base text-muted-foreground mt-1">
              🔐 Configure client credentials for SharePoint integration (Required for document storage)
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="microsoft-client-id" className="text-base font-semibold">
                Client ID *
              </Label>
              <Input
                id="microsoft-client-id"
                type="text"
                placeholder="Application (client) ID from Azure Portal"
                value={microsoftConfig?.clientId || ''}
                onChange={(e) => setMicrosoftConfig(prev => ({ 
                  ...prev, 
                  clientId: e?.target?.value 
                }))}
                className="mt-2 text-base"
              />
            </div>

            <div>
              <Label htmlFor="microsoft-tenant-id" className="text-base font-semibold">
                Tenant ID *
              </Label>
              <Input
                id="microsoft-tenant-id"
                type="text"
                placeholder="Directory (tenant) ID from Azure Portal"
                value={microsoftConfig?.tenantId || ''}
                onChange={(e) => setMicrosoftConfig(prev => ({ 
                  ...prev, 
                  tenantId: e?.target?.value 
                }))}
                className="mt-2 text-base"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="microsoft-client-secret" className="text-base font-semibold">
              Client Secret (Stored Securely) *
            </Label>
            <Input
              id="microsoft-client-secret"
              type="password"
              placeholder="Client secret from Azure Portal (stored encrypted)"
              value={microsoftConfig?.clientSecret || ''}
              onChange={(e) => setMicrosoftConfig(prev => ({ 
                ...prev, 
                clientSecret: e?.target?.value 
              }))}
              className="mt-2 text-base"
            />
            <div className="flex items-center gap-2 mt-2">
              <AlertCircle size={16} className="text-warning" />
              <p className="text-sm text-muted-foreground">
                Client secret is encrypted and only accessible by Supabase Edge Functions
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="microsoft-site-id" className="text-base font-semibold">
              Default SharePoint Site ID (Optional)
            </Label>
            <Input
              id="microsoft-site-id"
              type="text"
              placeholder="SharePoint site ID for default document storage"
              value={microsoftConfig?.siteId || ''}
              onChange={(e) => setMicrosoftConfig(prev => ({ 
                ...prev, 
                siteId: e?.target?.value 
              }))}
              className="mt-2 text-base"
            />
          </div>

          <div className="bg-primary/10 border-2 border-primary/30 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Info size={20} className="text-white" />
              </div>
              <h3 className="text-lg font-bold text-foreground">
                Azure Portal Configuration Required
              </h3>
            </div>
            <ul className="text-base text-foreground space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span><strong>Authentication Type:</strong> Client Credentials (Application-only)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span><strong>API Permissions:</strong> Sites.Selected (Application permission)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span><strong>Admin Consent:</strong> Grant admin consent for Sites.Selected permission</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span><strong>Site Registration:</strong> Register specific SharePoint sites in Azure Portal</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>No delegated permissions required (User.Read, Files.ReadWrite.All removed)</span>
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-between pt-6 border-t-2 border-primary/20">
            <div className="flex items-center gap-3">
              {microsoftConfig?.configured ? (
                <>
                  <div className="w-10 h-10 bg-success rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-lg text-success font-bold block">
                      Configured Successfully
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Microsoft Graph credentials are active
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-warning rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-lg text-warning font-bold block">
                      Configuration Required
                    </span>
                    <span className="text-sm text-muted-foreground">
                      Add credentials to enable SharePoint integration
                    </span>
                  </div>
                </>
              )}
            </div>

            <Button
              onClick={handleSaveMicrosoftConfig}
              disabled={
                savingMicrosoft || 
                !microsoftConfig?.clientId || 
                !microsoftConfig?.clientSecret || 
                !microsoftConfig?.tenantId
              }
              className="px-8 py-6 text-base font-bold"
              size="lg"
            >
              {savingMicrosoft ? 'Saving Configuration...' : 'Save Microsoft Configuration'}
            </Button>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-t-2 border-border my-8"></div>

      {/* API Configuration Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Code" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">API Configuration</h3>
            <p className="text-sm text-muted-foreground">Configure REST API settings and access controls</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Checkbox
              label="Enable REST API"
              description="Allow external applications to access the API"
              checked={apiSettings?.enableApi}
              onChange={(e) => handleApiChange('enableApi', e?.target?.checked)}
            />

            <Select
              label="API Version"
              description="Active API version for new integrations"
              options={apiVersionOptions}
              value={apiSettings?.apiVersion}
              onChange={(value) => handleApiChange('apiVersion', value)}
              disabled={!apiSettings?.enableApi}
            />

            <Checkbox
              label="Enable rate limiting"
              description="Limit API requests per client"
              checked={apiSettings?.rateLimitEnabled}
              onChange={(e) => handleApiChange('rateLimitEnabled', e?.target?.checked)}
              disabled={!apiSettings?.enableApi}
            />

            {apiSettings?.rateLimitEnabled && (
              <Input
                label="Requests per minute"
                type="number"
                value={apiSettings?.requestsPerMinute}
                onChange={(e) => handleApiChange('requestsPerMinute', parseInt(e?.target?.value))}
                min={100}
                max={10000}
                disabled={!apiSettings?.enableApi}
              />
            )}
          </div>

          <div className="space-y-4">
            <Checkbox
              label="Require authentication"
              description="All API requests must be authenticated"
              checked={apiSettings?.requireAuthentication}
              onChange={(e) => handleApiChange('requireAuthentication', e?.target?.checked)}
              disabled={!apiSettings?.enableApi}
            />

            <Checkbox
              label="Allow CORS"
              description="Enable cross-origin resource sharing"
              checked={apiSettings?.allowCors}
              onChange={(e) => handleApiChange('allowCors', e?.target?.checked)}
              disabled={!apiSettings?.enableApi}
            />

            {apiSettings?.allowCors && (
              <Input
                label="Allowed origins"
                value={apiSettings?.corsOrigins}
                onChange={(e) => handleApiChange('corsOrigins', e?.target?.value)}
                description="Comma-separated list of allowed domains"
                disabled={!apiSettings?.enableApi}
              />
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={generateApiKey}
                disabled={!apiSettings?.enableApi}
                iconName="Key"
                iconPosition="left"
              >
                Generate API Key
              </Button>
            </div>
          </div>
        </div>

        {apiSettings?.enableApi && (
          <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Info" size={16} className="text-accent" />
              <span className="text-sm font-medium text-accent">API Endpoint</span>
            </div>
            <p className="text-sm text-muted-foreground font-mono">
              https://api.zentridms.com/{apiSettings?.apiVersion}
            </p>
          </div>
        )}
      </div>
      {/* Authentication Methods Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-success" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Authentication Methods</h3>
            <p className="text-sm text-muted-foreground">Configure supported authentication mechanisms</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">API Authentication</h4>
            <div className="space-y-3">
              <Checkbox
                label="API Key Authentication"
                description="Simple API key-based authentication"
                checked={authenticationSettings?.apiKeyAuth}
                onChange={(e) => handleAuthChange('apiKeyAuth', e?.target?.checked)}
                disabled={!apiSettings?.enableApi}
              />
              <Checkbox
                label="OAuth 2.0"
                description="Industry-standard authorization framework"
                checked={authenticationSettings?.oauthEnabled}
                onChange={(e) => handleAuthChange('oauthEnabled', e?.target?.checked)}
                disabled={!apiSettings?.enableApi}
              />
              <Checkbox
                label="JWT Tokens"
                description="JSON Web Token authentication"
                checked={authenticationSettings?.jwtEnabled}
                onChange={(e) => handleAuthChange('jwtEnabled', e?.target?.checked)}
                disabled={!apiSettings?.enableApi}
              />
              <Checkbox
                label="Basic Authentication"
                description="Username/password authentication (not recommended)"
                checked={authenticationSettings?.basicAuthEnabled}
                onChange={(e) => handleAuthChange('basicAuthEnabled', e?.target?.checked)}
                disabled={!apiSettings?.enableApi}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-foreground">Token Settings</h4>
            <Input
              label="Token expiration (seconds)"
              type="number"
              value={authenticationSettings?.tokenExpiration}
              onChange={(e) => handleAuthChange('tokenExpiration', parseInt(e?.target?.value))}
              min={300}
              max={86400}
              description="How long tokens remain valid"
              disabled={!apiSettings?.enableApi}
            />

            <Checkbox
              label="Enable refresh tokens"
              description="Allow tokens to be refreshed without re-authentication"
              checked={authenticationSettings?.refreshTokens}
              onChange={(e) => handleAuthChange('refreshTokens', e?.target?.checked)}
              disabled={!apiSettings?.enableApi}
            />

            <h4 className="text-sm font-medium text-foreground mt-6">Single Sign-On</h4>
            <Checkbox
              label="Enable SSO"
              description="Allow single sign-on integration"
              checked={authenticationSettings?.ssoEnabled}
              onChange={(e) => handleAuthChange('ssoEnabled', e?.target?.checked)}
            />

            {authenticationSettings?.ssoEnabled && (
              <Select
                label="SSO Provider"
                options={ssoProviderOptions}
                value={authenticationSettings?.ssoProvider}
                onChange={(value) => handleAuthChange('ssoProvider', value)}
              />
            )}

            <Checkbox
              label="Enable LDAP"
              description="Integrate with LDAP directory services"
              checked={authenticationSettings?.ldapEnabled}
              onChange={(e) => handleAuthChange('ldapEnabled', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Webhook Configuration Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Webhook" size={20} className="text-warning" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Webhook Endpoints</h3>
              <p className="text-sm text-muted-foreground">Configure webhooks for real-time event notifications</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={addWebhookEndpoint}
            iconName="Plus"
            iconPosition="left"
            disabled={webhookEndpoints?.length >= apiSettings?.maxWebhooks}
          >
            Add Webhook
          </Button>
        </div>

        <div className="space-y-4">
          {webhookEndpoints?.map((webhook) => (
            <div key={webhook?.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${webhook?.active ? 'bg-success' : 'bg-muted-foreground'}`} />
                  <Input
                    value={webhook?.name}
                    onChange={(e) => handleWebhookUpdate(webhook?.id, 'name', e?.target?.value)}
                    className="font-medium"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => testWebhook(webhook?.id)}
                    iconName="Send"
                    disabled={!webhook?.active || !webhook?.url}
                  >
                    Test
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeWebhookEndpoint(webhook?.id)}
                    iconName="Trash2"
                  >
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <Input
                    label="Webhook URL"
                    value={webhook?.url}
                    onChange={(e) => handleWebhookUpdate(webhook?.id, 'url', e?.target?.value)}
                    placeholder="https://api.example.com/webhooks"
                  />

                  <Input
                    label="Secret Key"
                    value={webhook?.secret}
                    onChange={(e) => handleWebhookUpdate(webhook?.id, 'secret', e?.target?.value)}
                    description="Used to verify webhook authenticity"
                  />

                  <Checkbox
                    label="Active"
                    description="Enable this webhook endpoint"
                    checked={webhook?.active}
                    onChange={(e) => handleWebhookUpdate(webhook?.id, 'active', e?.target?.checked)}
                  />
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Events to Send</label>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {availableEvents?.map((event) => (
                        <Checkbox
                          key={event?.value}
                          label={event?.label}
                          checked={webhook?.events?.includes(event?.value)}
                          onChange={(e) => {
                            const events = e?.target?.checked
                              ? [...webhook?.events, event?.value]
                              : webhook?.events?.filter(ev => ev !== event?.value);
                            handleWebhookUpdate(webhook?.id, 'events', events);
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {webhook?.lastTriggered && (
                    <div className="text-sm text-muted-foreground">
                      Last triggered: {webhook?.lastTriggered?.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {webhookEndpoints?.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Webhook" size={48} className="mx-auto mb-4 opacity-50" />
              <p>No webhook endpoints configured</p>
              <p className="text-sm">Add a webhook to receive real-time event notifications</p>
            </div>
          )}
        </div>
      </div>
      {/* External Services Section */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
            <Icon name="Plug" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">External Service Integrations</h3>
            <p className="text-sm text-muted-foreground">Configure connections to external services and platforms</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Microsoft Office Integration */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center">
                  <Icon name="FileText" size={16} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Microsoft Office 365</h4>
                  <p className="text-sm text-muted-foreground">Word, Excel, PowerPoint integration</p>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${externalServices?.microsoftOffice?.configured ? 'bg-success' : 'bg-muted-foreground'}`} />
            </div>

            <div className="space-y-3">
              <Checkbox
                label="Enable Microsoft Office integration"
                checked={externalServices?.microsoftOffice?.enabled}
                onChange={(e) => handleServiceChange('microsoftOffice', 'enabled', e?.target?.checked)}
              />

              {externalServices?.microsoftOffice?.enabled && (
                <>
                  <Input
                    label="Client ID"
                    value={externalServices?.microsoftOffice?.clientId}
                    onChange={(e) => handleServiceChange('microsoftOffice', 'clientId', e?.target?.value)}
                    placeholder="Application (client) ID"
                  />
                  <Input
                    label="Tenant ID"
                    value={externalServices?.microsoftOffice?.tenantId}
                    onChange={(e) => handleServiceChange('microsoftOffice', 'tenantId', e?.target?.value)}
                    placeholder="Directory (tenant) ID"
                  />
                </>
              )}
            </div>
          </div>

          {/* Google Workspace Integration */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-accent/10 rounded flex items-center justify-center">
                  <Icon name="FileSpreadsheet" size={16} className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Google Workspace</h4>
                  <p className="text-sm text-muted-foreground">Docs, Sheets, Slides integration</p>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${externalServices?.googleWorkspace?.configured ? 'bg-success' : 'bg-muted-foreground'}`} />
            </div>

            <div className="space-y-3">
              <Checkbox
                label="Enable Google Workspace integration"
                checked={externalServices?.googleWorkspace?.enabled}
                onChange={(e) => handleServiceChange('googleWorkspace', 'enabled', e?.target?.checked)}
              />

              {externalServices?.googleWorkspace?.enabled && (
                <>
                  <Input
                    label="Client ID"
                    value={externalServices?.googleWorkspace?.clientId}
                    onChange={(e) => handleServiceChange('googleWorkspace', 'clientId', e?.target?.value)}
                    placeholder="OAuth 2.0 client ID"
                  />
                  <Input
                    label="Project ID"
                    value={externalServices?.googleWorkspace?.projectId}
                    onChange={(e) => handleServiceChange('googleWorkspace', 'projectId', e?.target?.value)}
                    placeholder="Google Cloud project ID"
                  />
                </>
              )}
            </div>
          </div>

          {/* Slack Integration */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-success/10 rounded flex items-center justify-center">
                  <Icon name="MessageSquare" size={16} className="text-success" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Slack</h4>
                  <p className="text-sm text-muted-foreground">Team notifications and updates</p>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${externalServices?.slack?.configured ? 'bg-success' : 'bg-muted-foreground'}`} />
            </div>

            <div className="space-y-3">
              <Checkbox
                label="Enable Slack notifications"
                checked={externalServices?.slack?.enabled}
                onChange={(e) => handleServiceChange('slack', 'enabled', e?.target?.checked)}
              />

              {externalServices?.slack?.enabled && (
                <>
                  <Input
                    label="Webhook URL"
                    value={externalServices?.slack?.webhookUrl}
                    onChange={(e) => handleServiceChange('slack', 'webhookUrl', e?.target?.value)}
                    placeholder="https://hooks.slack.com/services/..."
                  />
                  <Input
                    label="Default Channel"
                    value={externalServices?.slack?.channel}
                    onChange={(e) => handleServiceChange('slack', 'channel', e?.target?.value)}
                    placeholder="#channel-name"
                  />
                </>
              )}
            </div>
          </div>

          {/* Email Integration */}
          <div className="border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-warning/10 rounded flex items-center justify-center">
                  <Icon name="Mail" size={16} className="text-warning" />
                </div>
                <div>
                  <h4 className="font-medium text-foreground">Email Service</h4>
                  <p className="text-sm text-muted-foreground">SMTP email notifications</p>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${externalServices?.email?.configured ? 'bg-success' : 'bg-muted-foreground'}`} />
            </div>

            <div className="space-y-3">
              <Checkbox
                label="Enable email notifications"
                checked={externalServices?.email?.enabled}
                onChange={(e) => handleServiceChange('email', 'enabled', e?.target?.checked)}
              />

              {externalServices?.email?.enabled && (
                <>
                  <Input
                    label="SMTP Server"
                    value={externalServices?.email?.smtpServer}
                    onChange={(e) => handleServiceChange('email', 'smtpServer', e?.target?.value)}
                    placeholder="smtp.company.com"
                  />
                  <Input
                    label="SMTP Port"
                    type="number"
                    value={externalServices?.email?.smtpPort}
                    onChange={(e) => handleServiceChange('email', 'smtpPort', parseInt(e?.target?.value))}
                    placeholder="587"
                  />
                  <Input
                    label="Username"
                    value={externalServices?.email?.username}
                    onChange={(e) => handleServiceChange('email', 'username', e?.target?.value)}
                    placeholder="noreply@company.com"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex items-center justify-between p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="AlertTriangle" size={20} className="text-warning" />
            <span className="text-sm font-medium text-warning">You have unsaved changes</span>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset}>
              Reset Changes
            </Button>
            <Button variant="default" onClick={handleSave}>
              Save Integration Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationSettings;